import { CheckIcon } from '@heroicons/react/16/solid';
import { useGridContext } from 'admin/src/components/ui/DataGrid/DataGridContext';
import { EMPTY_FILTER_VALUE } from 'admin/src/components/ui/DataGrid/filters/agGridAdapter';
import { determineNewOperator } from 'admin/src/components/ui/DataGrid/filters/operators';
import type { FilterOption, FilterableColumn } from 'admin/src/components/ui/DataGrid/filters/types';
import type { ValuePanelProps } from 'admin/src/components/ui/DataGrid/filters/values/ValuePanelProps';
import { useMemo, useState } from 'react';
import { Command, CommandInput, CommandItem, CommandList } from 'shared/src/components/ui';
import { cn } from 'shared/src/utils';

const RENDER_CAP = 200;

export function OptionValues({ column, operator, values, options, autoFocus = true, onChange, onEmptyBackspace }: ValuePanelProps & { options: FilterOption[] }) {
  const [search, setSearch] = useState('');

  const { visible, truncated } = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const matches = needle ? options.filter((option) => option.label.toLowerCase().includes(needle) || option.value.toLowerCase().includes(needle)) : options;

    const capped = matches.slice(0, RENDER_CAP);
    const cappedValues = new Set(capped.map((option) => option.value));
    // Keep selected values reachable so a filter can always be undone from here.
    const selectedBeyondCap = matches.filter((option) => values.includes(option.value) && !cappedValues.has(option.value));

    return { visible: [...capped, ...selectedBeyondCap], truncated: matches.length > capped.length };
  }, [options, search, values]);

  function toggle(value: string) {
    const isSelected = values.includes(value);
    const next = isSelected ? values.filter((item) => item !== value) : [...values, value];

    onChange(determineNewOperator(column.dataType, values.length, next.length, operator), next);
  }

  return (
    <Command shouldFilter={false}>
      <CommandInput
        autoFocus={autoFocus}
        placeholder={`Search ${column.title.toLowerCase()}…`}
        value={search}
        onValueChange={setSearch}
        onKeyDown={(event) => {
          if (event.key === 'Backspace' && !search && onEmptyBackspace) {
            event.preventDefault();
            onEmptyBackspace();
          }
        }}
      />
      <CommandList
        className="overscroll-contain touch-pan-y [-webkit-overflow-scrolling:touch]"
        onWheel={(event) => event.stopPropagation()}
        onTouchMove={(event) => event.stopPropagation()}
      >
        {visible.length ? (
          visible.map((option) => (
            <CommandItem key={option.value} value={option.value} onSelect={() => toggle(option.value)} className="gap-2">
              <span
                className={cn(
                  'flex size-4 shrink-0 items-center justify-center rounded-sm border border-enhancetitle',
                  values.includes(option.value) ? 'bg-primary text-primary-foreground' : 'opacity-50 [&_svg]:invisible',
                )}
              >
                <CheckIcon className="size-3" />
              </span>
              <span className="truncate">
                <OptionLabel column={column} option={option} />
              </span>
              {option.count ? <sup className="ml-auto pl-2 font-mono text-[10px] tabular-nums text-muted-foreground">{option.count}</sup> : null}
            </CommandItem>
          ))
        ) : (
          <div className="py-6 text-center text-sm text-muted-foreground">No values match</div>
        )}
        {truncated ? <div className="border-t px-2 py-1.5 text-center text-xs text-muted-foreground">Refine your search to see more</div> : null}
      </CommandList>
    </Command>
  );
}

function OptionLabel({ column, option }: { column: FilterableColumn; option: FilterOption }) {
  const { gridApi } = useGridContext();

  const rendered = useMemo(() => {
    const valueFormatter = column.colDef.filterParams?.valueFormatter;
    if (!valueFormatter || !gridApi || gridApi.isDestroyed()) return undefined;

    const gridColumn = gridApi.getColumn(column.columnId);
    const cellRenderer = gridColumn?.getColDef()?.cellRenderer;

    try {
      const valueFormatted = valueFormatter({ column: gridColumn, value: option.value });
      return cellRenderer ? cellRenderer({ value: option.value, valueFormatted }) : valueFormatted;
    } catch {
      return undefined;
    }
  }, [column, gridApi, option.value]);

  if (rendered) return rendered;

  if (option.value === EMPTY_FILTER_VALUE) {
    return <span className="text-[13px] font-semibold italic text-muted-foreground">{option.label}</span>;
  }

  return option.label;
}
